import { FC } from "react";
import { Button, ConfigProvider, Popover, Switch } from "antd";
// @ts-ignore
import { FormattedNumber } from "@umijs/max";
import classNames from "classnames";

import styles from "./index.less";

import { ReactComponent as CheckIcon } from "../assets/svg/check.svg";
import { ReactComponent as UncheckIcon } from "../assets/svg/uncheck.svg";

import {
  PricingPeriodEnum,
  PricingPlanVersionEnum,
  PricingPlanVersionNumMap,
} from "../types";
import { useVersionWithPrice } from "../hooks";
import { isInPlatform } from "../utils";

import { BlackTheme } from "@packages/theme";
interface FeaturesProps {
  paiedVersion?: PricingPlanVersionEnum;
  period: PricingPeriodEnum;
  isExpired?: boolean;
  onTogglePeriod: (period: PricingPeriodEnum) => void;
  onSubscribe: (version: PricingPlanVersionEnum) => void;
  onRenew: (version: PricingPlanVersionEnum) => void;
  onUpgrade: (targetVersion: PricingPlanVersionEnum) => void;
}

const Features: FC<FeaturesProps> = ({
  paiedVersion,
  period,
  isExpired,
  onTogglePeriod,
  onSubscribe,
  onRenew,
  onUpgrade,
}) => {
  const { array: VersionWithPrice } = useVersionWithPrice();

  const paiedVersionNum = paiedVersion
    ? PricingPlanVersionNumMap[paiedVersion]
    : 0;

  return (
    <div className={styles.features}>
      <div className={styles.title}>各个版本的功能对比</div>
      <div className={styles.table}>
        <ConfigProvider theme={BlackTheme}>
          <div
            className={styles.header}
            style={{
              top: isInPlatform() ? 0 : 50,
            }}
          >
            <div className={styles.description}>
              <div className={styles.title}>权益对照表</div>
              <div className={styles.yearly}>
                按年订阅
                <Switch
                  size="small"
                  checked={period === PricingPeriodEnum.YEARLY}
                  onChange={(e) => {
                    onTogglePeriod(
                      e ? PricingPeriodEnum.YEARLY : PricingPeriodEnum.MONTHLY
                    );
                  }}
                />
              </div>
            </div>

            {/* 不同版本的权益 */}
            {VersionWithPrice.map((p) => (
              <div key={p.version} className={styles.item}>
                <div className={styles.version}>{p.label}</div>
                <div className={styles.price}>
                  <div className={styles.num}>
                    ¥
                    <FormattedNumber
                      value={
                        period === PricingPeriodEnum.MONTHLY
                          ? p.monthlyPrice
                          : p.yearlyPrice
                      }
                    />
                  </div>
                  <div className={styles.unit}>
                    {period === PricingPeriodEnum.MONTHLY ? "/月" : "/年"}
                  </div>
                </div>

                {(paiedVersion === void 0 || isExpired) && (
                  <Button
                    className={styles.button}
                    onClick={() => {
                      onSubscribe(p.version);
                    }}
                  >
                    立即订阅
                  </Button>
                )}

                {!isExpired &&
                  !!paiedVersion &&
                  paiedVersionNum === p.versionNum && (
                    <Button
                      type="default"
                      className={styles.button}
                      onClick={() => {
                        onRenew(p.version);
                      }}
                    >
                      续费订阅
                    </Button>
                  )}

                {!isExpired &&
                  !!paiedVersion &&
                  paiedVersionNum < p.versionNum && (
                    <Button
                      type="default"
                      className={styles.button}
                      onClick={() => {
                        onUpgrade(p.version);
                      }}
                    >
                      立即升级
                    </Button>
                  )}

                {!isExpired &&
                  !!paiedVersion &&
                  paiedVersionNum > p.versionNum && (
                    <Popover content="若您需要订阅低版本服务，可前往资源中心的「自动续费」模块完成相关设置。">
                      <Button type="default" className={styles.button} disabled>
                        更改订阅
                      </Button>
                    </Popover>
                  )}
              </div>
            ))}
          </div>
        </ConfigProvider>

        <div className={styles.body}>
          <div className={styles.feature}>
            <div className={styles.category}>机器人</div>
            <div className={styles["row-single"]}>
              <div className={styles["row-title"]}>机器人（支持自主训练）</div>
              <div className={styles.cell}>-</div>
              <div className={styles.cell}>10个</div>
              <div className={styles.cell}>30个</div>
              <div className={styles.cell}>不限量</div>
            </div>

            <div className={styles["row-multiple"]}>
              <div className={styles["row-title"]}>预置机器人</div>
              <div className={styles["row-right"]}>
                <div className={styles.cell}>问答机器人</div>
                <div className={styles.cell}>问答机器人</div>
                <div className={styles.cell}>问答机器人</div>
                <div className={styles.cell}>问答机器人</div>

                <div className={styles.cell}>接待机器人</div>
                <div className={styles.cell}>接待机器人</div>
                <div className={styles.cell}>接待机器人</div>
                <div className={styles.cell}>接待机器人</div>

                <div className={styles.cell}>
                  <UncheckIcon />
                </div>
                <div className={styles.cell}>促成跟进机器人</div>
                <div className={styles.cell}>促成跟进机器人</div>
                <div className={styles.cell}>促成跟进机器人</div>

                <div className={styles.cell}>
                  <UncheckIcon />
                </div>
                <div className={styles.cell}>
                  销售总结机器人
                  <br />
                  （客户维度）
                </div>
                <div className={styles.cell}>
                  销售总结机器人
                  <br />
                  （客户/账号/组织维度）
                </div>
                <div className={styles.cell}>
                  销售总结机器人
                  <br />
                  （客户/账号/组织维度）
                </div>

                <div className={styles.cell}>
                  <UncheckIcon />
                </div>
                <div className={styles.cell}>
                  <UncheckIcon />
                </div>
                <div className={styles.cell}>智能标签机器人</div>
                <div className={styles.cell}>智能标签机器人</div>

                <div className={styles.cell}>
                  <UncheckIcon />
                </div>
                <div className={styles.cell}>
                  <UncheckIcon />
                </div>
                <div className={styles.cell}>
                  <UncheckIcon />
                </div>
                <div className={styles.cell}>销售机器人</div>

                <div className={styles.cell}>
                  <UncheckIcon />
                </div>
                <div className={styles.cell}>
                  <UncheckIcon />
                </div>
                <div className={styles.cell}>
                  <UncheckIcon />
                </div>
                <div className={styles.cell}>客服机器人</div>
              </div>
            </div>

            <div className={styles["row-single"]}>
              <div className={styles["row-title"]}>
                动能值（动能值加量包：1点 = 0.05元）
              </div>
              {[1_000, 15_000, 50_000, 120_000].map((i) => (
                <div key={i} className={styles.cell}>
                  <FormattedNumber value={i} style="decimal" />
                  点/月
                </div>
              ))}
            </div>

            <div className={styles["row-single"]}>
              <div className={styles["row-title"]}>可编辑机器人流程账号</div>
              <div className={styles.cell}>
                <UncheckIcon />
              </div>
              <div className={styles.cell}>5个</div>
              <div className={styles.cell}>15个</div>
              <div className={styles.cell}>不限</div>
            </div>
          </div>

          <div className={styles.feature}>
            <div className={styles.category}>账号聚合管理</div>
            <div className={styles["row-single"]}>
              <div className={styles["row-title"]}>多个社交账号统一管理</div>
              <div className={styles.cell}>
                <CheckIcon />
              </div>
              <div className={styles.cell}>
                <CheckIcon />
              </div>
              <div className={styles.cell}>
                <CheckIcon />
              </div>
              <div className={styles.cell}>
                <CheckIcon />
              </div>
            </div>
          </div>

          <div className={styles.feature}>
            <div className={styles.category}>客户管理功能</div>
            <div className={styles["row-single"]}>
              <div className={styles["row-title"]}>CRM</div>
              <div className={classNames(styles.cell, styles["cell-two-line"])}>
                <CheckIcon />
                <div>不支持API</div>
              </div>
              <div className={classNames(styles.cell, styles["cell-two-line"])}>
                <CheckIcon />
                <div>支持手动API调用</div>
              </div>
              <div className={classNames(styles.cell, styles["cell-two-line"])}>
                <CheckIcon />
                <div>支持手动API调用</div>
              </div>
              <div className={classNames(styles.cell, styles["cell-two-line"])}>
                <CheckIcon />
                <div>支持手动API调用</div>
              </div>
            </div>
            <div className={styles["row-single"]}>
              <div className={styles["row-title"]}>客户分组（动态/静态）</div>
              <div className={styles.cell}>
                <CheckIcon />
              </div>
              <div className={styles.cell}>
                <CheckIcon />
              </div>
              <div className={styles.cell}>
                <CheckIcon />
              </div>
              <div className={styles.cell}>
                <CheckIcon />
              </div>
            </div>
            <div className={styles["row-single"]}>
              <div className={styles["row-title"]}>客户标签管理</div>
              <div className={styles.cell}>手动打标</div>
              <div className={styles.cell}>标签单向同步</div>
              <div className={styles.cell}>标签双向同步</div>
              <div className={styles.cell}>标签双向同步</div>
            </div>
          </div>

          <div className={styles.feature}>
            <div className={styles.category}>客户运营功能</div>
            <div className={styles["row-single"]}>
              <div className={styles["row-title"]}>单场景群发</div>
              <div className={styles.cell}>
                <CheckIcon />
              </div>
              <div className={styles.cell}>
                <CheckIcon />
              </div>
              <div className={styles.cell}>
                <CheckIcon />
              </div>
              <div className={styles.cell}>
                <CheckIcon />
              </div>
            </div>

            <div className={styles["row-single"]}>
              <div className={styles["row-title"]}>客户全生命周期运营</div>
              <div className={classNames(styles.cell, styles["cell-two-line"])}>
                <CheckIcon />
                <div>不含机器人</div>
              </div>
              <div className={classNames(styles.cell, styles["cell-two-line"])}>
                <CheckIcon />
                <div>不含机器人</div>
              </div>
              <div className={classNames(styles.cell, styles["cell-two-line"])}>
                <CheckIcon />
                <div>接入机器人</div>
              </div>
              <div className={classNames(styles.cell, styles["cell-two-line"])}>
                <CheckIcon />
                <div>接入机器人</div>
              </div>
            </div>

            <div className={styles["row-single"]}>
              <div className={styles["row-title"]}>非业务场景智能运营</div>
              <div className={styles.cell}>
                <UncheckIcon />
              </div>
              <div className={styles.cell}>
                <UncheckIcon />
              </div>
              <div className={styles.cell}>
                <CheckIcon />
              </div>
              <div className={styles.cell}>
                <CheckIcon />
              </div>
            </div>

            <div className={styles["row-single"]}>
              <div className={styles["row-title"]}>运营消息数量</div>
              <div className={styles.cell}>1千条/次，1万条/月</div>
              <div className={styles.cell}>3万条/次，50万条/月</div>
              <div className={styles.cell}>5万条/次，100万条/月</div>
              <div className={styles.cell}>10万条/次，250万条/月</div>
            </div>
          </div>

          <div className={styles.feature}>
            <div className={styles.category}>系统集成能力</div>
            <div className={styles["row-single"]}>
              <div className={styles["row-title"]}>
                <Popover content="满足机器人调取客户数据" placement="top">
                  <div className={styles.dashed}>支持API接口</div>
                </Popover>
              </div>
              <div className={classNames(styles.cell, styles["cell-two-line"])}>
                <UncheckIcon />
                <div>不支持API</div>
              </div>
              <div className={classNames(styles.cell, styles["cell-two-line"])}>
                <CheckIcon />
                <div>不含机器人</div>
              </div>
              <div className={classNames(styles.cell, styles["cell-two-line"])}>
                <CheckIcon />
                <div>接入机器人</div>
              </div>
              <div className={classNames(styles.cell, styles["cell-two-line"])}>
                <CheckIcon />
                <div>接入机器人</div>
              </div>
            </div>
            <div className={styles["row-single"]}>
              <div className={styles["row-title"]}>
                <Popover
                  content="满足其他程序调用机器人和智能运营"
                  placement="top"
                >
                  <div className={styles.dashed}>支持API调用</div>
                </Popover>
              </div>
              <div className={styles.cell}>
                <UncheckIcon />
              </div>
              <div className={styles.cell}>
                <UncheckIcon />
              </div>
              <div className={styles.cell}>每分钟 ≤ 60次</div>
              <div className={styles.cell}>每分钟 ≤ 100次</div>
            </div>
            <div className={styles["row-single"]}>
              <div className={styles["row-title"]}>
                <Popover
                  content="满足机器人自动调用各类线上工具完成任务"
                  placement="top"
                >
                  <div className={styles.dashed}>支持MCP调用</div>
                </Popover>
              </div>

              <div className={styles.cell}>
                <UncheckIcon />
              </div>
              <div className={styles.cell}>
                <CheckIcon />
              </div>
              <div className={styles.cell}>
                <CheckIcon />
              </div>
              <div className={styles.cell}>
                <CheckIcon />
              </div>
            </div>
            <div className={styles["row-single"]}>
              <div className={styles["row-title"]}>
                <Popover
                  content="满足机器人操作电脑系统抓取数据或完成任务"
                  placement="top"
                >
                  <div className={styles.dashed}>支持RPA联通</div>
                </Popover>
              </div>

              <div className={styles.cell}>
                <UncheckIcon />
              </div>
              <div className={styles.cell}>
                <CheckIcon />
              </div>
              <div className={styles.cell}>
                <CheckIcon />
              </div>
              <div className={styles.cell}>
                <CheckIcon />
              </div>
            </div>
          </div>

          <div className={styles.feature}>
            <div className={styles.category}>培训课程</div>
            <div className={styles["row-multiple"]}>
              <div className={styles["row-title"]}>在线课程</div>
              <div className={styles["row-right"]}>
                <div className={styles.cell}>《提示词工程》</div>
                <div className={styles.cell}>《提示词工程》</div>
                <div className={styles.cell}>《提示词工程》</div>
                <div className={styles.cell}>《提示词工程》</div>

                <div className={styles.cell}>《机器人参数调试指南》</div>
                <div className={styles.cell}>《机器人参数调试指南》</div>
                <div className={styles.cell}>《机器人参数调试指南》</div>
                <div className={styles.cell}>《机器人参数调试指南》</div>

                <div className={styles.cell}>
                  《玩转智能运营，产出最好效果》
                </div>
                <div className={styles.cell}>
                  《玩转智能运营，产出最好效果》
                </div>
                <div className={styles.cell}>
                  《玩转智能运营，产出最好效果》
                </div>
                <div className={styles.cell}>
                  《玩转智能运营，产出最好效果》
                </div>

                <div className={styles.cell}>
                  <UncheckIcon />
                </div>
                <div className={styles.cell}>《编排指南》</div>
                <div className={styles.cell}>《编排指南》</div>
                <div className={styles.cell}>《编排指南》</div>
              </div>
            </div>
            <div className={styles["row-single"]}>
              <div className={styles["row-title"]}>线下课程</div>
              <div
                className={classNames(styles.cell, styles["cell-three-line"])}
              >
                <UncheckIcon />
              </div>
              <div
                className={classNames(styles.cell, styles["cell-three-line"])}
              >
                <div>《企业高管AI实践课》</div>
                <div>享受1人免费名额</div>
              </div>
              <div
                className={classNames(styles.cell, styles["cell-three-line"])}
              >
                <div>《企业高管AI实践课》</div>
                <div>享受1人免费名额 + 3个5折名额</div>
                <div>专题驻场培训享受8折优惠</div>
              </div>
              <div
                className={classNames(styles.cell, styles["cell-three-line"])}
              >
                <div>《企业高管AI实践课》</div>
                <div>享受5人免费名额</div>
                <div>专题驻场培训享受6折优惠</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Features;
